import { createSelector } from 'reselect';
import { IDLE, PENDING } from 'constants/Status';
import get from 'utils/get';
import _isEmpty from 'lodash/isEmpty';

import { userIsAuthenticated, orderValidations } from 'state/selectors';

const guestCustomerCanOrder = createSelector(
  state => get(state, 'brandibble.session.order.orderData', {}),
  state => get(state, 'brandibble.status.createOrder', IDLE),
  state => get(state, 'brandibble.status.submitOrder', IDLE),
  state => orderValidations(state),
  (orderData, createOrderStatus, submitOrderStatus, orderValidations) => {
    if (_isEmpty(orderData)) return false;

    // If the customer is missing required attributes, return false
    const firstName = get(orderData, 'customer.first_name', '');
    const lastName = get(orderData, 'customer.last_name', '');
    const email = get(orderData, 'customer.email', '');
    const phone = get(orderData, 'customer.phone', '');

    if (!firstName) return false;
    if (!lastName) return false;
    if (!email) return false;
    if (!phone) return false;

    // If the customer hasn't added a credit card, return false
    if (_isEmpty(get(orderData, 'credit_card', {}))) {
      return false;
    }

    // If order validations are present, return false
    if (orderValidations.length) {
      // Catering orders force a password in the validation step. However,
      // we expect our users to have entered a valid password, so we let this
      // validation slide.
      if (
        orderValidations.length !== 1 ||
        orderValidations[0].code !== 'orders.validate.missing_password'
      ) {
        return false;
      }
    }

    // If a new order is being created OR an order is being submitted, return false
    if (createOrderStatus === PENDING || submitOrderStatus === PENDING) {
      return false;
    }

    return true;
  }
);

const authenticatedCustomerCanOrder = createSelector(
  state => get(state, 'brandibble.session.order.orderData', {}),
  state => get(state, 'brandibble.user.attributes.phone', null),
  state => get(state, 'brandibble.status.createOrder', IDLE),
  state => get(state, 'brandibble.status.submitOrder', IDLE),
  (orderData, phoneNumber, createOrderStatus, submitOrderStatus) => {
    // If the customer hasn't added a phone number, return false
    if (!phoneNumber) return false;

    // If the customer hasn't added a credit card, return false
    if (_isEmpty(get(orderData, 'credit_card', {}))) {
      return false;
    }

    // If a new order is being created OR an order is being submitted, return false
    if (createOrderStatus === PENDING || submitOrderStatus === PENDING) {
      return false;
    }

    // If order validations are present, return false
    if (orderValidations.length) return false;

    return true;
  }
);

const canSubmitOrder = state =>
  userIsAuthenticated(state)
    ? authenticatedCustomerCanOrder(state)
    : guestCustomerCanOrder(state);

export default canSubmitOrder;
